import { Layout } from "@layouts";
import { Bar } from "@organisms";
import ReactModal from "react-modal";
import { Button } from "@atoms";
import { Card, PageBreadcrumbs } from "@molecules";
import { PagePaths } from "../../../../types";
import { styles as globalStyles } from "../../../../styles/styles";
import { useTranslation } from "react-i18next";

export type LifestyleConfirmPageTemplateProps = {
  navigateToPage: (to: string) => void;
  onReturnPressed: () => void;
  onConfirm: () => void;
  onConfirmCreate: () => void;
  closeModal: () => void;
  selectedCategories: Array<{ icon: string; title: string }>;
  yearlyAmount: number;
  coveragePercentage: number;
  allocationType: 'pro-rata' | 'full-amount';
  lifestyleGroupName: string;
  isModalOpen: boolean;
  isLoading: boolean;
};

export const LifestyleConfirmPageTemplate = ({
  navigateToPage,
  onReturnPressed,
  onConfirm,
  onConfirmCreate,
  closeModal,
  selectedCategories,
  yearlyAmount,
  coveragePercentage,
  allocationType,
  lifestyleGroupName,
  isModalOpen,
  isLoading
}: LifestyleConfirmPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const goToPerks = () => navigateToPage(`/dashboard/${PagePaths.perks}`);
  const goToLifestyleMain = () => navigateToPage(`/dashboard/${PagePaths.lifestyle}/main`);

  const baseModalStyles = globalStyles.generalModal || {};
  const baseContentStyles = (baseModalStyles && baseModalStyles.content) || {};
  const confirmModalStyle = {
    ...baseModalStyles,
    content: {
      ...baseContentStyles,
      width:
        (typeof baseContentStyles.width === "number"
          ? baseContentStyles.width
          : 400) + 80,
    },
  };

  return (
    <Layout
      variant="dashboard"
      header={t("lifestyleConfirm.header")}
      subheader={
        <div className="flex flex-row justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("lifestyleConfirm.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("lifestyleConfirm.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("lifestyleConfirm.breadcrumbs.lifestyle"), onClick: goToLifestyleMain },
            { label: t("lifestyleConfirm.breadcrumbs.confirm") },
          ]}
        />
        <div className="flex flex-col pb-24">
          {/* Numbered Title */}
          <div className="mb-6 mt-2">
            <h2 className="text-neutral-darkest text-xl font-normal mb-2">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  4.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("lifestyleConfirm.title")}
                </span>
              </div>
            </h2>
            <p className="text-gray-600">
              {t("lifestyleConfirm.description")}
            </p>
          </div>

          {/* Summary Sections */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Lifestyle Group Name Summary */}
              <div
                className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col min-h-[520px]"
              >
                <div className="overflow-auto">
                  {/* <h3 className="text-xl font-semibold text-gray-900 mb-4">Lifestyle Group Name</h3> */}
                  <div className="p-2 pl-0 rounded-md">
                    <p className="text-2xl font-medium text-neutral-darkest">{lifestyleGroupName}</p>
                    {/* <p className="text-sm text-gray-600 mt-1">This name will be visible to your employees</p> */}
                  </div>
                  <div className="mt-2">
                    {/* <h4 className="text-xl font-semibold text-gray-900 mb-3">General Rules</h4> */}
                    <div className="space-y-3">
                    <Card
                      variant="metric"
                      label={t("lifestyleConfirm.yearlyAmount")}
                      value={`$${yearlyAmount.toLocaleString()} / ${t("common.year") ?? "year"}`}
                      className="bg-white border-gray-200"
                      valueClassName="text-xl text-successful-dark"
                      labelClassName="text-md"
                    />
                    <Card
                      variant="metric"
                      label={t("lifestyleConfirm.coverage")}
                      value={`${coveragePercentage}%`}
                      className="bg-white border-gray-200"
                      valueClassName="text-xl text-successful-dark"
                      labelClassName="text-md"
                    />
                  </div>
              </div>
              <div className="mt-3">
                <Card
                  variant="metric"
                  label={
                    allocationType === "full-amount"
                      ? t("lifestyleRules.allocationOptions.fullAmountDescription")
                      : t("lifestyleRules.allocationOptions.proRataDescription")
                  }
                  value={`${allocationType === "full-amount" ? t("lifestyleRules.allocationOptions.fullAmountTitle") : t("lifestyleRules.allocationOptions.proRataTitle")} ${t("lifestyleConfirm.allocation")}`}
                  className="bg-white border-gray-200"
                  valueClassName="text-xl text-successful-dark"
                  labelClassName="text-md"
                  helperClassName="text-xs text-gray-600"
                />
              </div>
            </div>
            <div className="pt-8 pb-2 w-60">
              <Button
                onClick={onConfirm}
                variant="primary"
                label={t("common.continue")}
                stretch
                textWeight="semibold"
                color="primary-medium"
              />
            </div>
            </div>

              {/* Categories Summary */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-dark mb-4">
                  {`${t("lifestyleConfirm.categories")} (${selectedCategories.length})`}
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {selectedCategories.map((category, index) => (
                    <div key={index} className="flex items-center p-3 bg-white border border-primary-mediumish rounded-xl">
                      <span className="text-xl mr-2">{category.icon}</span>
                      <span className="text-sm font-medium text-gray-900 truncate">{category.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={null}
          isContinueDisabled={false}
          // altContinueText="Launch Program"
        />

        {/* Confirmation Modal */}
        {isModalOpen && (
          <ReactModal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            style={confirmModalStyle}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
          >
            <div>
              <div className="flex flex-col items-center">
                <div className="font-semibold text-2xl flex flex-row justify-center py-6">
                  Confirm Lifestyle Group Creation
                </div>
                <div className="mb-8 flex flex-col justify-center w-80 text-center space-y-2">
                  <p className="text-gray-700">
                    You are about to create a lifestyle group
                  </p>
                  <p className="text-xl text-gray-700">
                    <strong>{lifestyleGroupName}</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    This will set up automated reimbursements for {selectedCategories.length} categories with a {allocationType === "full-amount" ? "yearly" : "prorated yearly"} limit of ${yearlyAmount.toLocaleString()} per employee.
                  </p>
                </div>
                <div className="flex flex-row justify-center space-x-4 pb-4">
                  <div className="w-32">
                    <Button
                      onClick={closeModal}
                      variant="secondary"
                      label={t("common.goBack")}
                      stretch
                      color="white"
                    />
                  </div>
                  <div className="w-32">
                    <Button
                      onClick={isLoading ? undefined : onConfirmCreate}
                      variant="primary"
                      label={t("common.continue")}
                      stretch
                      textWeight="semibold"
                      color="primary-medium"
                      loading={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </ReactModal>
        )}
      </div>
    </Layout>
  );
}; 
